import React, { useState, useEffect } from 'react';
import { DeviceCreate } from '../../api/models/DeviceCreate';
import { DeviceTypeEnum } from '../../api/models/DeviceTypeEnum';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { TemplateForDevice, deviceService } from '../../services/deviceService';
import { typesService, HardwareType, ModelReference, HardwareFieldDefinition } from '../../services/typesService';
import { getTypeBadgeColor } from '../../utils/fieldTypes';
import { Client } from '../../types/client';
import { CustomField } from '../../types/configTemplate';
import { DeviceSpecificFields } from '../common/DeviceSpecificFields';
import SearchableSelect from '../common/SearchableSelect';
import { useI18n } from '../../contexts/I18nContext';
import './CreateDeviceModal.css';

interface CreateDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeviceCreated: (device: DeviceCreate) => void;
}

interface FormData {
  idDevice: string;
  idClient: string;
  deviceType: string;
  model: string;
  syslogHost: string;
  syslogPort: string;
  syslogFacility: string;
  provisioning: string;
  keepaliveTimer: string;
  password: string;
  mqttHost: string;
  mqttPort: string;
  mqttUser: string;
  mqttPass: string;
  dateTime: string;
  phone: string;
  needsRefresh: boolean;
}

interface FormErrors {
  [key: string]: string;
}

/**
 * Maps a hardware-type field_name (snake_case, what's stored in
 * hardware_field_definitions) to the camelCase key on FormData that it
 * drives. Field names NOT in this map go into `customFieldValues` and
 * ultimately into jsonAdditionalConfig on the API payload.
 */
const FIELD_NAME_TO_FORM_KEY: Record<string, keyof FormData> = {
  mqtt_host: 'mqttHost',
  mqtt_port: 'mqttPort',
  mqtt_user: 'mqttUser',
  mqtt_pass: 'mqttPass',
  syslog_host: 'syslogHost',
  syslog_port: 'syslogPort',
  syslog_facility: 'syslogFacility',
  keepalive_timer: 'keepaliveTimer',
  password: 'password',
  provisioning: 'provisioning',
  phone: 'phone',
};

const DEFAULT_GROUPING_NAME = 'General';

export const CreateDeviceModal: React.FC<CreateDeviceModalProps> = ({
  isOpen,
  onClose,
  onDeviceCreated
}) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  // Hardware types and model refs for Section 1
  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  const [modelRefs, setModelRefs] = useState<ModelReference[]>([]);

  // Templates for Section 3
  const [templates, setTemplates] = useState<TemplateForDevice[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [maxTemplatePriority, setMaxTemplatePriority] = useState<string>('');
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Hardware type schema — drives which fields are shown in the form.
  // null = not loaded yet (either no type selected, or fetch in-flight).
  const [schema, setSchema] = useState<HardwareFieldDefinition[] | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  // Values for fields whose field_name is NOT in FIELD_NAME_TO_FORM_KEY —
  // stashed here and sent as jsonAdditionalConfig on the API payload.
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});

  // Device-specific custom fields (device type → template → device overlay)
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  const [formData, setFormData] = useState<FormData>({
    idDevice: '',
    idClient: '',
    deviceType: '',
    model: '',
    syslogHost: '',
    syslogPort: '',
    syslogFacility: '',
    provisioning: '',
    keepaliveTimer: '',
    password: '',
    mqttHost: '',
    mqttPort: '',
    mqttUser: '',
    mqttPass: '',
    dateTime: new Date().toISOString().slice(0, 16),
    phone: '',
    needsRefresh: false
  });

  // Fetch clients and hardware types on mount
  useEffect(() => {
    if (isOpen) {
      clientService.fetchActiveClients()
        .then(setClients)
        .catch(err => console.error('Failed to fetch clients:', err));

      // Active-only — a brand-new device must never be assigned to a
      // soft-deleted hardware type. The render also defends with
      // .filter(t => t.is_active), kept as belt-and-suspenders.
      typesService.getHardwareTypes()
        .then(setHardwareTypes)
        .catch(err => console.error('Failed to fetch hardware types:', err));
    }
  }, [isOpen]);

  // Load model refs when device type changes
  useEffect(() => {
    if (formData.deviceType) {
      const hwType = hardwareTypes.find(t => t.code === formData.deviceType);
      if (hwType) {
        typesService.getModelReferences(hwType.id)
          .then(setModelRefs)
          .catch(err => console.error('Failed to fetch model refs:', err));
      } else {
        setModelRefs([]);
      }
    } else {
      setModelRefs([]);
    }
    // Reset model when type changes
    setFormData(prev => ({ ...prev, model: '' }));
  }, [formData.deviceType, hardwareTypes]);

  // Load templates when client and device type are selected
  useEffect(() => {
    if (formData.idClient && formData.deviceType) {
      setTemplatesLoading(true);
      // For new devices, we need to fetch templates that match the device type
      // Since we don't have a device ID yet, we'll use a placeholder approach
      apiClient.get(`/v2/config-templates?device_type=${formData.deviceType}&client_id=${formData.idClient}`)
        .then((data: any) => {
          const items = Array.isArray(data) ? data : (data.items || []);
          setTemplates(items);
        })
        .catch(err => {
          console.error('Failed to fetch templates:', err);
          setTemplates([]);
        })
        .finally(() => setTemplatesLoading(false));
    } else {
      setTemplates([]);
      setSelectedTemplateId(null);
    }
  }, [formData.idClient, formData.deviceType]);

  // Load the hardware type field-definition schema whenever the selected
  // hardware type changes. This drives what the form displays — only the
  // fields defined for that type appear to the user.
  useEffect(() => {
    if (!formData.deviceType) {
      setSchema(null);
      setCustomFieldValues({});
      return;
    }
    const hwType = hardwareTypes.find((t) => t.code === formData.deviceType);
    if (!hwType) {
      setSchema(null);
      return;
    }
    setSchemaLoading(true);
    typesService
      .getHardwareFieldDefinitions(hwType.id)
      .then((response) => {
        const sorted = [...response.items].sort((a, b) => {
          const groupCmp = (a.grouping_name || DEFAULT_GROUPING_NAME).localeCompare(
            b.grouping_name || DEFAULT_GROUPING_NAME,
          );
          if (groupCmp !== 0) return groupCmp;
          return (a.display_order || 0) - (b.display_order || 0);
        });
        setSchema(sorted);

        // Don't pre-fill defaults — fields start blank so the user
        // explicitly chooses what to set. Defaults are shown as
        // placeholders instead.
      })
      .catch((err) => {
        console.error('Failed to fetch hardware type schema:', err);
        setSchema([]);
      })
      .finally(() => setSchemaLoading(false));
  }, [formData.deviceType, hardwareTypes]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Always-required identity fields
    if (!formData.idDevice.trim()) newErrors.idDevice = t('errors.fieldRequired');
    if (!formData.idClient.trim()) newErrors.idClient = t('errors.fieldRequired');
    if (!formData.deviceType.trim()) newErrors.deviceType = t('errors.fieldRequired');

    // Device ID format — allow MAC-style colons and dotted identifiers
    // alongside the original alphanumerics + dash + underscore.
    if (formData.idDevice && !/^[a-zA-Z0-9_:.-]+$/.test(formData.idDevice)) {
      newErrors.idDevice = t('errors.invalidDeviceId');
    }

    // Schema-driven required fields. We only validate fields that are
    // actually shown to the user (i.e. defined on the selected hardware
    // type's schema). Ports get an extra range check.
    if (schema) {
      for (const def of schema) {
        if (!def.is_required) continue;
        const formKey = FIELD_NAME_TO_FORM_KEY[def.field_name];
        const raw = formKey ? formData[formKey] : customFieldValues[def.field_name];
        const strVal = typeof raw === 'string' ? raw : raw != null ? String(raw) : '';
        if (!strVal.trim()) {
          newErrors[def.field_name] = t('errors.fieldRequired');
          continue;
        }
        if (def.value_type === 'port' || def.field_name.endsWith('_port')) {
          const n = Number(strVal);
          if (isNaN(n) || n <= 0 || n > 65535) {
            newErrors[def.field_name] = t('errors.invalidPort');
          }
        }
        if (def.value_type === 'number') {
          const n = Number(strVal);
          if (isNaN(n)) {
            newErrors[def.field_name] = t('errors.fieldRequired');
          } else {
            if (def.min_value != null && n < def.min_value) {
              newErrors[def.field_name] = `Minimum ${def.min_value}`;
            }
            if (def.max_value != null && n > def.max_value) {
              newErrors[def.field_name] = `Maximum ${def.max_value}`;
            }
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Read/write a single field value whether it lives in formData or in
  // customFieldValues, based on whether its field_name is a known
  // DeviceCreate key.
  const getFieldValue = (def: HardwareFieldDefinition): string => {
    const formKey = FIELD_NAME_TO_FORM_KEY[def.field_name];
    if (formKey) {
      const v = formData[formKey];
      return typeof v === 'string' ? v : v != null ? String(v) : '';
    }
    return customFieldValues[def.field_name] ?? '';
  };

  const setFieldValue = (def: HardwareFieldDefinition, value: string) => {
    const formKey = FIELD_NAME_TO_FORM_KEY[def.field_name];
    if (formKey) {
      setFormData((prev) => ({ ...prev, [formKey]: value }));
    } else {
      setCustomFieldValues((prev) => ({ ...prev, [def.field_name]: value }));
    }
    if (errors[def.field_name]) {
      setErrors((prev) => ({ ...prev, [def.field_name]: '' }));
    }
  };

  /** Populate every schema field that declares a default_value with that
   *  default. Fields without a default are left untouched. */
  const fillDefaultValues = () => {
    if (!schema) return;
    for (const def of schema) {
      if (def.default_value != null && def.default_value !== '') {
        setFieldValue(def, String(def.default_value));
      }
    }
  };

  /** Whether any visible field declares a default — gates the fill button. */
  const hasFillableDefaults = React.useMemo(
    () => !!schema && schema.some((d) => d.default_value != null && d.default_value !== ''),
    [schema],
  );

  /** Parse a field definition's options column (JSON string OR already-parsed array) */
  const parseOptions = (opt: unknown): string[] => {
    if (!opt) return [];
    if (Array.isArray(opt)) return opt.map(String);
    if (typeof opt === 'string') {
      try {
        const parsed = JSON.parse(opt);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch {
        /* noop */
      }
    }
    return [];
  };

  const renderFieldRow = (def: HardwareFieldDefinition): React.ReactNode => {
    const value = getFieldValue(def);
    const error = errors[def.field_name];
    // Show the default value as placeholder so the user sees what
    // the assembly pipeline will use if the field is left blank.
    const placeholder = def.default_value
      ? `default: ${def.default_value}`
      : def.placeholder || '—';

    let control: React.ReactNode;
    switch (def.value_type) {
      case 'boolean':
        control = (
          <label className="cell-checkbox" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
            <input type="checkbox" disabled={isLoading} checked={value === 'true'} onChange={(e) => setFieldValue(def, e.target.checked ? 'true' : 'false')} />
            <span style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary, #6b7280)' }}>{value === 'true' ? 'Yes' : 'No'}</span>
          </label>
        );
        break;
      case 'password':
        control = <input type="password" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={isLoading} onChange={(e) => setFieldValue(def, e.target.value)} />;
        break;
      case 'port':
      case 'number':
        control = <input type="text" aria-label={def.field_name} inputMode="numeric" className="cell-input mono" value={value} placeholder={placeholder} disabled={isLoading} onChange={(e) => setFieldValue(def, e.target.value.replace(/[^\d]/g, ''))} />;
        break;
      case 'dropdown':
        control = (
          <select aria-label={def.field_name} className="cell-select" value={value} disabled={isLoading} onChange={(e) => setFieldValue(def, e.target.value)}>
            <option value="">{placeholder}</option>
            {parseOptions(def.options).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
        break;
      default:
        control = <input type="text" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={isLoading} onChange={(e) => setFieldValue(def, e.target.value)} />;
    }

    return (
      <tr key={def.id} className={error ? 'row-invalid' : ''}>
        <td><code>{def.field_name}</code></td>
        <td><span className="type-badge" style={{ backgroundColor: getTypeBadgeColor(def.value_type) }}>{def.value_type}</span></td>
        <td>{control}{error && <div className="field-error" style={{ fontSize: '0.7rem', marginTop: 2 }}>{error}</div>}</td>
      </tr>
    );
  };

  // Group fields by grouping_name for section rendering.
  const groupedSchema: Array<{ name: string; fields: HardwareFieldDefinition[] }> = React.useMemo(() => {
    if (!schema) return [];
    const byGroup = new Map<string, HardwareFieldDefinition[]>();
    for (const def of schema) {
      const g = def.grouping_name || DEFAULT_GROUPING_NAME;
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g)!.push(def);
    }
    return Array.from(byGroup.entries()).map(([name, fields]) => ({ name, fields }));
  }, [schema]);

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const generateRandomPassword = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from(crypto.getRandomValues(new Uint8Array(12)))
      .map(b => chars[b % chars.length])
      .join('');
  };

  const applyTemplateValues = async (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    setSelectedTemplateId(templateId);

    // Fetch full template details to get json_config
    try {
      const fullTemplate = await apiClient.get(`/v2/config-templates/${templateId}`);
      const jsonConfig = fullTemplate.json_config || {};

      // Determine password value from template (generate_password flag
      // takes precedence, otherwise use the explicit password, otherwise
      // leave the user's current input untouched).
      let templatePassword: string | undefined;
      if (jsonConfig.generate_password === true) {
        templatePassword = generateRandomPassword();
      } else if (jsonConfig.password) {
        templatePassword = jsonConfig.password;
      }

      // Generic partitioning: every top-level key in json_config is
      // routed to either formData (if it maps to a known DeviceCreate
      // key via FIELD_NAME_TO_FORM_KEY) or customFieldValues (for
      // schema-only fields like `controller`, `test42`, `mqtt_special`
      // that live on the hardware type's schema but NOT in the
      // hardcoded DeviceCreate column set). Template-metadata keys
      // (generate_password, password, custom_fields) are excluded
      // from this loop and handled separately below.
      const TEMPLATE_METADATA_KEYS = new Set([
        'generate_password',
        'password',
        'custom_fields',
      ]);
      const formPatch: Record<string, string> = {};
      const customPatch: Record<string, string> = {};
      for (const [key, rawValue] of Object.entries(jsonConfig)) {
        if (TEMPLATE_METADATA_KEYS.has(key)) continue;
        if (rawValue === undefined || rawValue === null) continue;
        const strValue =
          typeof rawValue === 'string'
            ? rawValue
            : typeof rawValue === 'boolean' || typeof rawValue === 'number'
              ? String(rawValue)
              : JSON.stringify(rawValue);
        const formKey = FIELD_NAME_TO_FORM_KEY[key];
        if (formKey) {
          formPatch[formKey as string] = strValue;
        } else {
          customPatch[key] = strValue;
        }
      }

      // Merge the partitioned patches into state, preserving the
      // special-case password handling.
      setFormData((prev) => {
        const next = { ...prev, ...formPatch } as typeof prev;
        if (templatePassword !== undefined) {
          next.password = templatePassword;
        }
        return next;
      });
      setCustomFieldValues((prev) => ({ ...prev, ...customPatch }));

      // Legacy support: templates created via the old DeviceSpecificFields
      // editor store fields as { custom_fields: [{name, value, ...}] }.
      // Mirror those into customFieldValues so the schema-driven renderer
      // shows them (and keep the CustomField list for any downstream
      // consumers that still read it).
      if (jsonConfig.custom_fields && Array.isArray(jsonConfig.custom_fields)) {
        const fields: CustomField[] = jsonConfig.custom_fields.map((f: CustomField) => ({
          ...f,
          source: 'template' as const,
          default: f.default ?? f.value,
        }));
        setCustomFields(fields);
        setCustomFieldValues((prev) => {
          const next = { ...prev };
          for (const f of fields) {
            if (f.name && f.value !== undefined && f.value !== null) {
              next[f.name] = typeof f.value === 'string' ? f.value : String(f.value);
            }
          }
          return next;
        });
      }

      // Device-type defaults used to be merged here via
      // typesService.getConfigTypes(), but that API no longer exists —
      // defaults now live on the hardware-type schema (field_definitions)
      // and are surfaced by the config-assembly pipeline at read time.
      // The initial customFields setter above (line ~504) is sufficient.
    } catch (err) {
      console.error('Failed to fetch template details:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    const deviceData: DeviceCreate = {
      idDevice: formData.idDevice.trim(),
      idClient: formData.idClient.trim(),
      syslogHost: formData.syslogHost.trim(),
      syslogPort: Number(formData.syslogPort),
      syslogFacility: formData.syslogFacility.trim(),
      provisioning: formData.provisioning.trim(),
      keepaliveTimer: Number(formData.keepaliveTimer),
      password: formData.password.trim(),
      mqttHost: formData.mqttHost.trim(),
      mqttPort: Number(formData.mqttPort),
      mqttUser: formData.mqttUser.trim(),
      mqttPass: formData.mqttPass.trim(),
      dateTime: new Date(formData.dateTime),
      deviceType: formData.deviceType.trim() ? (formData.deviceType as DeviceTypeEnum) : undefined,
      phone: formData.phone.trim() || null,
      needsRefresh: formData.needsRefresh || null
    };

    // Build API payload. Legacy config keys (syslog_*, mqtt_*, provisioning,
    // keepalive_timer) are optional on the backend and schema-driven via the
    // hardware type's field definitions — send them only when the form holds
    // a real value, never inject static placeholders (514/'local0'/1883).
    //
    // Schema fields whose name is NOT in the core set are sent in a
    // SECOND call (/v2/conf/{id}/override-config) after the device is
    // created, because DeviceCreate.jsonAdditionalConfig is typed as
    // Optional[List[str]] in Pydantic and can't carry {name, value} pairs.
    const customFieldList = Object.entries(customFieldValues)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([name, value]) => ({ name, value }));

    const optStr = (v: string | undefined | null) =>
      v && v.trim() !== '' ? v : undefined;
    // Numeric form inputs are strings; an empty field must mean "absent",
    // not Number('') === 0 (which the backend would reject as an invalid port).
    const optNum = (v: string) => {
      const trimmed = v.trim();
      if (trimmed === '') return undefined;
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : undefined;
    };

    const apiPayload: Record<string, any> = {
      idDevice: deviceData.idDevice,
      idClient: deviceData.idClient,
      syslog_host: optStr(deviceData.syslogHost),
      syslog_port: optNum(formData.syslogPort),
      syslog_facility: optStr(deviceData.syslogFacility),
      provisioning: optStr(deviceData.provisioning),
      keepalive_timer: optNum(formData.keepaliveTimer),
      password: deviceData.password || '',
      mqtt_host: optStr(deviceData.mqttHost),
      mqtt_port: optNum(formData.mqttPort),
      mqtt_user: optStr(deviceData.mqttUser),
      mqtt_pass: optStr(deviceData.mqttPass),
      date_time: deviceData.dateTime.toISOString(),
      deviceType: deviceData.deviceType || null,
      phone: deviceData.phone || null,
      needsRefresh: deviceData.needsRefresh === true ? true : false,
      model_ref: formData.model || null,
      template_id: selectedTemplateId || null,
      max_template_priority: maxTemplatePriority ? Number(maxTemplatePriority) : null,
    };
    // Drop the keys the form left empty so the request carries no
    // phantom legacy fields.
    Object.keys(apiPayload).forEach((k) => {
      if (apiPayload[k] === undefined) delete apiPayload[k];
    });

    try {
      const response = await apiClient.getAxiosInstance().post('/v2/conf', apiPayload);

      if (response.status === 200 || response.status === 201) {
        // Follow-up: if the form collected custom fields (schema entries
        // whose name is NOT in the core DeviceCreate set), persist them
        // via the override-config endpoint. This keeps the create payload
        // compliant with DeviceCreate's strict Pydantic type.
        if (customFieldList.length > 0) {
          try {
            await deviceService.overrideConfig(deviceData.idDevice, {
              custom_fields: customFieldList,
            });
          } catch (overrideErr: any) {
            console.error('[CREATE DEVICE] Custom fields override failed:', overrideErr);
            // Device was created successfully; surface a non-fatal warning.
            setErrors({
              submit:
                'Device created, but custom fields could not be saved: ' +
                (overrideErr?.response?.data?.detail || overrideErr?.message || 'unknown error'),
            });
            setIsLoading(false);
            return;
          }
        }

        onDeviceCreated(deviceData);
        onClose();
        resetForm();
      }
    } catch (error: any) {
      console.error('[CREATE DEVICE] Error:', error);

      let errorMessage = t('errors.createDeviceFailed');

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          if (detail.includes('ForeignKeyViolation') && detail.includes('client')) {
            errorMessage = 'Client ID does not exist. Please select an existing client.';
          } else if (detail.includes('duplicate key') || detail.includes('already exists')) {
            errorMessage = `Device ID '${formData.idDevice}' already exists.`;
          } else {
            errorMessage = detail;
          }
        }
      }

      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      idDevice: '',
      idClient: '',
      deviceType: '',
      model: '',
      syslogHost: '',
      syslogPort: '',
      syslogFacility: '',
      provisioning: '',
      keepaliveTimer: '',
      password: '',
      mqttHost: '',
      mqttPort: '',
      mqttUser: '',
      mqttPass: '',
      dateTime: new Date().toISOString().slice(0, 16),
      phone: '',
      needsRefresh: false
    });
    setSelectedTemplateId(null);
    setMaxTemplatePriority('');
    setCustomFields([]);
    setErrors({});
    setSchema(null);
    setCustomFieldValues({});
    setCustomFields([]);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-device-modal create-device-modal-v2" data-testid="modal-create-device" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-title-section">
            <h2>{t('devices.addDevice')}</h2>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        {errors.submit && (
          <div className="error-banner">
            <span>{errors.submit}</span>
            <button onClick={() => setErrors(prev => ({ ...prev, submit: '' }))}>×</button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-content device-sections">
            {/* Section 1: Device Identity */}
            <div className="device-section device-identity-section">
              <h3 className="section-title">{t('devices.deviceIdentity')}</h3>
              <div className="identity-fields-create">
                <div className="form-field">
                  <label className="required" htmlFor="idDevice">{t('devices.deviceId')}</label>
                  <input
                    id="idDevice"
                    name="idDevice"
                    data-testid="field-device-id"
                    type="text"
                    value={formData.idDevice}
                    onChange={(e) => handleInputChange('idDevice', e.target.value.toUpperCase())}
                    className={`field-input ${errors.idDevice ? 'error' : ''}`}
                    placeholder={t('devices.deviceIdFormat')}
                    maxLength={64}
                  />
                  {errors.idDevice && <span className="field-error">{errors.idDevice}</span>}
                </div>

                <div className="form-field">
                  <label className="required">{t('devices.clientLabel')}</label>
                  <SearchableSelect
                    value={formData.idClient}
                    data-testid="field-device-client"
                    onChange={(next) => handleInputChange('idClient', next)}
                    className={`field-select ${errors.idClient ? 'error' : ''}`}
                    placeholder={t('devices.selectClient')}
                    options={clients.filter(c => c.active !== false).map(client => ({ value: String(client.id), label: client.name }))}
                  />
                  {errors.idClient && <span className="field-error">{errors.idClient}</span>}
                </div>

                <div className="form-field">
                  <label>{t('devices.typeLabel')}</label>
                  <SearchableSelect
                    value={formData.deviceType}
                    data-testid="field-device-type"
                    onChange={(next) => handleInputChange('deviceType', next)}
                    className="field-select"
                    placeholder={t('devices.selectType')}
                    options={hardwareTypes.filter(t => t.is_active).map(type => ({ value: type.code, label: `${type.name} (${type.code})` }))}
                  />
                </div>

                <div className="form-field">
                  <label>{t('devices.modelLabel')}</label>
                  <SearchableSelect
                    value={formData.model}
                    onChange={(next) => handleInputChange('model', next)}
                    className="field-select"
                    disabled={!formData.deviceType || modelRefs.length === 0}
                    placeholder={t('devices.selectModel')}
                    options={modelRefs.filter(r => r.is_active).map(ref => ({ value: ref.code, label: `${ref.code}${ref.name ? ` - ${ref.name}` : ''}` }))}
                  />
                </div>
              </div>
            </div>

            {/* Sections 2 + 3 are gated behind hardware-type selection */}
            {!formData.deviceType ? (
              <div className="device-section">
                <p className="template-hint" style={{ padding: 16, textAlign: 'center' }}>
                  {t('devices.selectHardwareHint')}
                </p>
              </div>
            ) : schemaLoading ? (
              <div className="device-section">
                <p className="template-hint" style={{ padding: 16, textAlign: 'center' }}>
                  {t('devices.loadingSchema', { type: formData.deviceType })}
                </p>
              </div>
            ) : (
              <>
                {/* Section 2: Configuration Template (only after type is selected) */}
                <div className="device-section config-section">
                  <h3 className="section-title">{t('devices.configurationTemplate')}</h3>
                  <div className="config-normal">
                    <div className="template-controls">
                      <SearchableSelect
                        value={selectedTemplateId != null ? String(selectedTemplateId) : ''}
                        onChange={(next) => {
                          const id = next ? Number(next) : null;
                          if (id) {
                            applyTemplateValues(id);
                          } else {
                            setSelectedTemplateId(null);
                          }
                        }}
                        disabled={templatesLoading || !formData.idClient}
                        className="template-select"
                        placeholder={t('devices.noTemplate')}
                        options={templates.map((template) => ({ value: String(template.id), label: `${template.name} (priority ${template.priority})` }))}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                      <label style={{ color: 'var(--theme-text-secondary, #6b7280)', whiteSpace: 'nowrap' }}>
                        {t('devices.maxTemplatePriority')}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="cell-input mono"
                        style={{ width: '60px', textAlign: 'center' }}
                        value={maxTemplatePriority}
                        placeholder={t('devices.maxPriorityNone')}
                        onChange={(e) => setMaxTemplatePriority(e.target.value.replace(/\D/g, '').slice(0, 3))}
                        disabled={isLoading}
                      />
                      <span style={{ color: 'var(--theme-text-tertiary, #9ca3af)', fontSize: '0.75rem' }}>
                        {maxTemplatePriority ? t('devices.maxPriorityExplanation', { value: maxTemplatePriority }) : t('devices.allTemplatesApply')}
                      </span>
                    </div>
                    {!formData.idClient ? (
                      <p className="template-hint">{t('devices.selectClientForTemplates')}</p>
                    ) : templates.length === 0 && !templatesLoading ? (
                      <p className="template-hint">
                        {t('devices.noTemplatesAvailable', { type: formData.deviceType })}
                      </p>
                    ) : selectedTemplateId ? (
                      <p className="template-hint template-applied">
                        {t('devices.templateValuesApplied')}
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* Section 3: Fields defined by the hardware type's schema */}
                <div className="device-section connection-section">
                  <div className="section-title-row">
                    <h3 className="section-title">{t('devices.configurationFields')}</h3>
                    {hasFillableDefaults && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        data-testid="btn-fill-defaults"
                        onClick={fillDefaultValues}
                        disabled={isLoading}
                        title={t('devices.fillDefaultsTitle')}
                      >
                        {t('devices.fillDefaults')}
                      </button>
                    )}
                  </div>

                  {schema && schema.length === 0 ? (
                    <p className="template-hint" style={{ padding: 12 }}>
                      {t('devices.noFieldDefsConfigured')}{' '}
                      <strong>{formData.deviceType}</strong>.{t('devices.fieldDefsHint')}
                    </p>
                  ) : (
                    <div className="field-defs-list">
                      {groupedSchema.map((group) => (
                        <div className="field-group" key={group.name}>
                          <h4 className="group-header">{group.name}</h4>
                          <table className="field-defs-table">
                            <thead>
                              <tr>
                                <th>{t('devices.fieldName')}</th>
                                <th>{t('devices.typeLabel')}</th>
                                <th>{t('devices.value')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.fields.map((def) => renderFieldRow(def))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Section 4: Device-Specific Custom Fields */}
                {customFields.length > 0 && (
                  <div className="device-section">
                    <DeviceSpecificFields
                      fields={customFields}
                      onChange={setCustomFields}
                      isEditing={true}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Modal Footer */}
          <div className="modal-actions">
            <div className="action-group-left">
              <button type="button" onClick={onClose} className="btn btn-secondary" data-testid="btn-cancel" disabled={isLoading}>
                Cancel
              </button>
            </div>
            <div className="action-group-right">
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create Device'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
